import { Fragment, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { useStaticQuery, graphql } from 'gatsby'
import { useQuery } from 'react-query'
import {
  Container,
  Inline,
  List,
  ListItem,
  Section,
  Badge,
} from 'chaoskit/src/components'
import useIntersection from 'react-use/lib/useIntersection'
import { gradient, text } from 'chaoskit/src/assets/styles/utility'

import SocialGroup from '../global/SocialGroup'

import FooterCTA from './FooterCTA'

import Data from '~components/Data'
import TextLink from '~components/TextLink'
import { ReactComponent as TechCrunchSVG } from '~media/logos/techcrunch.svg'
import { ReactComponent as ForbesSVG } from '~media/logos/forbes.svg'
import { ReactComponent as VentureBeatSVG } from '~media/logos/venturebeat.svg'
import { ReactComponent as BusinessInsiderSVG } from '~media/logos/business-insider.svg'

const Status = () => {
  const theme = useTheme()
  const ref = useRef()
  const [visible, setVisible] = useState(false)

  const intersection = useIntersection(ref, {
    rootMargin: '0px',
    threshold: 0.25,
  })

  useEffect(() => {
    if (intersection?.isIntersecting && !visible) {
      setVisible(true)
    }
  }, [intersection, visible])

  const { isIdle, isLoading, error, data } = useQuery(
    'status',
    () => fetch('/.netlify/functions/status').then((res) => res.json()),
    {
      enabled: visible,
    }
  )

  if (error) return null

  return (
    <div
      ref={ref}
      css={{
        background:
          data?.combined === 'down'
            ? theme.color.danger.light
            : theme.color.primary.light,
        borderRadius: theme.borderRadius.base,
        fontFamily: theme.fontFamily.mono,
        color:
          data?.combined === 'down'
            ? theme.color.danger.base
            : theme.color.primary.base,
        marginTop: theme.space.large,
        fontWeight: theme.fontWeight.bold,
        display: 'inline-flex',
        padding: theme.space.small,
        lineHeight: 1,
        alignItems: 'center',
        position: 'relative',
      }}
    >
      <div
        css={{
          background: 'currentColor',
          borderRadius: 9999,
          width: theme.space.small,
          height: theme.space.small,
          marginRight: theme.space.small,
        }}
      />
      {/* eslint-disable-next-line no-nested-ternary */}
      {isLoading || isIdle
        ? 'Loading...'
        : data.combined === 'up'
        ? 'All systems operational'
        : 'Issue detected'}
      <TextLink
        href="https://status.gremlin.com"
        title="View Gremlin status"
        css={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        }}
      />
    </div>
  )
}

const FooterLink = ({ link }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        display: 'inline-grid',
        gap: theme.space.small,
        gridAutoColumns: 'auto',
        gridAutoFlow: 'column',
        alignItems: 'center',
      }}
    >
      <TextLink className="u-link" href={link.url}>
        {link.label}
      </TextLink>
      {link.badge && <Badge type="primary">{link.badge}</Badge>}
    </div>
  )
}

FooterLink.propTypes = {
  link: PropTypes.object.isRequired,
}

const FooterHR = () => {
  const theme = useTheme()

  return (
    <hr
      css={{
        marginTop: theme.space.large,
        marginBottom: theme.space.large,

        [theme.mq.medium]: {
          marginTop: theme.space.large + theme.space.base,
          marginBottom: theme.space.large + theme.space.base,
        },
      }}
    />
  )
}

const Footer = ({ hideCTA }) => {
  const theme = useTheme()

  const {
    file: {
      childMdx: { frontmatter: pageData },
    },
  } = useStaticQuery(graphql`
    query FooterPageData {
      file(name: { eq: "footer" }, absolutePath: { regex: "/src/data/" }) {
        childMdx {
          frontmatter {
            linkGroups {
              heading
              links {
                label
                url
                badge
              }
            }
            social {
              title
              service
              url
            }
          }
        }
      }
    }
  `)

  return (
    <Fragment>
      {!hideCTA && <FooterCTA />}
      <Section
        as="footer"
        css={{ ...gradient.dark(theme) }}
        className="u-contrast"
      >
        <Container>
          <Inline
            css={{
              justifyContent: 'space-between',

              a: {
                display: 'inline-block',
                opacity: theme.opacity.base,
                transition: `opacity ${theme.timing.base} ${theme.transition.base}`,

                '&:hover, &:focus': {
                  opacity: 1,
                },
              },

              svg: {
                filter: 'brightness(0) invert(1)',
              },
            }}
            size="medium"
          >
            <ListItem>
              <TextLink
                href="https://techcrunch.com/2018/02/04/the-rise-of-chaos-engineering/"
                title="TechCrunch"
              >
                <TechCrunchSVG style={{ height: 22 }} />
              </TextLink>
            </ListItem>
            <ListItem>
              <TextLink
                href="https://www.forbes.com/sites/forbestechcouncil/2018/04/26/why-ctos-and-cios-should-care-more-about-the-cost-of-downtime/#272f69eb131c"
                title="Forbes"
              >
                <ForbesSVG style={{ height: 26 }} />
              </TextLink>
            </ListItem>
            <ListItem>
              <TextLink
                href="https://www.businessinsider.com/gremlin-free-chaos-engineering-netflix-amazon-2019-2"
                title="Business Insider"
              >
                <BusinessInsiderSVG style={{ height: 16 }} />
              </TextLink>
            </ListItem>
            <ListItem>
              <TextLink
                href="https://venturebeat.com/2017/12/12/gremlin-launches-to-help-companies-stress-test-their-apps/"
                title="VentureBeat"
              >
                <VentureBeatSVG style={{ height: 22 }} />
              </TextLink>
            </ListItem>
          </Inline>
          <FooterHR />
          <div
            css={{
              display: 'grid',
              gap: theme.space.large,
              alignContent: 'start',

              [theme.mq.medium]: {
                gridTemplateColumns: '1fr 0.5fr',
              },
            }}
          >
            <div>
              <div
                css={{
                  display: 'grid',
                  gap: theme.space.large,
                  justifyContent: 'center',
                  textAlign: 'center',

                  [theme.mq.small]: {
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    textAlign: 'left',
                  },

                  [theme.mq.medium]: {
                    justifyContent: 'start',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                  },
                }}
              >
                {pageData.linkGroups.map((linkGroup) => (
                  <div key={linkGroup.heading}>
                    <div
                      css={{
                        ...text.expanded(theme),
                        fontWeight: theme.fontWeight.bold,
                        marginBottom: theme.space.base,
                        opacity: theme.opacity.base,
                        ...theme.text.small,
                      }}
                    >
                      <Data>{linkGroup.heading}</Data>
                    </div>
                    <List space="base">
                      {linkGroup.links.map((link) => (
                        <ListItem key={link.label}>
                          <FooterLink link={link} />
                        </ListItem>
                      ))}
                    </List>

                    {/* Manual stub for Industries; previously in header nav. Part of product tour relaunch */}
                    {linkGroup.heading === 'Resources' && (
                      <div key={linkGroup.heading}>
                        <div
                          css={{
                            ...text.expanded(theme),
                            fontWeight: theme.fontWeight.bold,
                            marginBottom: theme.space.base,
                            ...theme.text.small,
                            opacity: theme.opacity.base,
                            marginTop: theme.space.large,
                          }}
                        >
                          Industries
                        </div>
                        <List space="base">
                          {[
                            {
                              label: 'SaaS',
                              url: '/industries/saas?ref=footer',
                            },
                            {
                              label: 'Finance',
                              url: '/industries/finance?ref=footer',
                            },
                            {
                              label: 'Retail',
                              url: '/industries/retail?ref=footer',
                            },
                          ].map((link) => (
                            <ListItem key={link.label}>
                              <FooterLink link={link} />
                            </ListItem>
                          ))}
                        </List>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',

                [theme.mq.medium]: {
                  alignItems: 'flex-end',
                },
              }}
            >
              <SocialGroup networks={pageData.social} />
              <Status />
            </div>
          </div>
          <FooterHR />
          <div css={{ textAlign: 'center', fontSize: theme.fontSize.small }}>
            &copy; {new Date().getFullYear()} Gremlin Inc. San Jose, CA 95113
          </div>
        </Container>
      </Section>
    </Fragment>
  )
}

Footer.propTypes = {
  hideCTA: PropTypes.bool,
}

export default Footer
